
import { NotificationCenter } from "@/components/NotificationCenter";
import { UserMenu } from "@/components/UserMenu";
import { cn } from "@/lib/utils";
import { Link } from "react-router-dom";
import { SetupProgressTracker } from "@/components/SetupProgressTracker";
import { useSetupProgress } from "@/hooks/useSetupProgress";
import { useIsAdmin } from '@/hooks/useAnalytics';
import { Button } from "@/components/ui/button";
import { ShieldCheck } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { useTheme } from "@/components/theme-provider";

interface HeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode;
}

export function Header({
  className,
  children,
  ...props
}: HeaderProps) {
  const { theme } = useTheme();
  const {
    setupSteps,
    isLoading,
    isLoggedIn
  } = useSetupProgress();
  
  const {
    data: isAdmin
  } = useIsAdmin();
  
  // Only show progress tracker if user is logged in, the data is loaded,
  // and there's at least one incomplete step
  const showProgressTracker = isLoggedIn && 
    !isLoading && 
    setupSteps.some(step => !step.completed);

  // Choose logo based on theme
  const logoSrc = theme === "dark" 
    ? "/lovable-uploads/smplify-logo-inverted.png" 
    : "/lovable-uploads/496f0ef7-a54a-4a74-a39b-0a3c3067bb55.png";

  return <header className={cn("flex h-16 items-center gap-4 border-b border-border bg-background px-4 overflow-hidden", className)} {...props}>
      {children}
      <div className="flex items-center ml-[-1rem]">
        <Link to="/getting-started">
          <img 
            src={logoSrc} 
            alt="Smplify Logo" 
            className="h-12 sm:h-16 md:h-20 w-auto cursor-pointer hover:opacity-90 transition-opacity object-contain" 
          />
        </Link>
      </div>
      <div className="hidden sm:flex items-center ml-[-1rem]">
        <Separator orientation="vertical" className="h-4 mx-1" />
        <span className="text-sm font-medium text-brand-main">Partner Portal</span>
      </div>
      <div className="flex-1" />
      <div className="flex items-center gap-2">
        {showProgressTracker && (
          <div className="hidden lg:block">
            <SetupProgressTracker steps={setupSteps} />
          </div>
        )}
        <NotificationCenter />
        <UserMenu />
      </div>
    </header>;
}
